import { useEffect, useState } from 'react'
import { Button } from 'blaze-ui/Button'
import { ArrowLeft, Close } from 'blaze-ui/icons'
import { VisualStylePreview } from 'components/VisualStylePreview/VisualStylePreview'
import { GenerationLoading } from 'screens/VirtualMarketerOnboarding/BrandVisualStyleSelection/GenerationLoading'
import { MediaFile } from 'components/VisualStylePreviewCarousel/VisualStylePreviewCarousel'
import { useFontPairings } from 'hooks/queries/fontPairings'
import { useFonts } from 'hooks/queries/workspaces/useFonts'
import { StyleGenerated } from 'screens/VirtualMarketerOnboarding/BrandVisualStyleSelection/GenerationStage'
import { trackEventInHeap, HeapEventType } from 'utils/heapTracking'
import Styles from '../../VisualStylesForExistingUsersModal.module.scss'

interface MobilePreviewStepProps {
  selectedStyle: StyleGenerated | null
  selectedFontPairingId: number | null
  brandColor?: string
  onNext: () => void
  onBack: () => void
  onClose: () => void
  onAdjustStyle: () => void
  onChangeFont: () => void
  isGeneratingStyle?: boolean
  sourceImage: MediaFile | null
  styledUrl: string | null
}

export const MobilePreviewStep = ({
  selectedStyle,
  selectedFontPairingId,
  brandColor,
  onNext,
  onBack,
  onClose,
  onAdjustStyle,
  onChangeFont,
  isGeneratingStyle = false,
  sourceImage,
  styledUrl,
}: MobilePreviewStepProps) => {
  const { data: fontPairingsData } = useFontPairings()
  const { data: fontsData } = useFonts()
  const customFonts = fontsData?.fonts ?? []
  const [transformedImageLoaded, setTransformedImageLoaded] = useState(false)

  useEffect(() => {
    trackEventInHeap(HeapEventType.VisualStylesForExistingUsers, {
      action: 'view-preview-step-mobile',
    })
  }, [])

  const handleSave = () => {
    trackEventInHeap(HeapEventType.VisualStylesForExistingUsers, {
      action: 'complete-preview-step-mobile',
    })
    onNext()
  }

  const renderPreviewContent = () => {
    if (isGeneratingStyle && sourceImage) {
      return (
        <div className={Styles.mobilePreviewImageContainer}>
          <GenerationLoading
            sourceImage={sourceImage}
            stylesGeneration={{
              styledUrl,
              isLoading: isGeneratingStyle,
              hasFailed: false,
            }}
            onTransformedImageLoaded={setTransformedImageLoaded}
            isLoading={isGeneratingStyle && !transformedImageLoaded}
            withTag={false}
          />
        </div>
      )
    }

    if (selectedStyle && fontPairingsData) {
      return (
        <div className={Styles.mobilePreviewImageContainer}>
          <VisualStylePreview
            fontPairingId={selectedFontPairingId}
            styleGenerated={selectedStyle}
            brandColor={brandColor}
            fontPairings={fontPairingsData}
            firstCustomFont={customFonts[0]}
          />
        </div>
      )
    }

    return (
      <div className={Styles.mobilePreviewImageContainer}>
        {selectedStyle?.cover_image_url && <img src={selectedStyle.cover_image_url} alt="Preview" />}
      </div>
    )
  }

  return (
    <div className={Styles.mobileStepContainer}>
      <div className={Styles.mobileStepHeader}>
        <Button size="sm" variant="tertiary" onPress={onBack}>
          <ArrowLeft size={20} />
        </Button>
        <Button size="sm" variant="tertiary" onPress={onClose}>
          <Close size={20} />
        </Button>
      </div>

      <div className={Styles.mobileStepContentCentered}>
        <div className={Styles.mobilePreviewContent}>
          {renderPreviewContent()}

          <div className={Styles.mobilePreviewActions}>
            <Button variant="ghost" size="sm" onPress={onAdjustStyle} className={Styles.mobilePreviewActionLink}>
              Adjust Style
            </Button>
            <Button variant="ghost" size="sm" onPress={onChangeFont} className={Styles.mobilePreviewActionLink}>
              Change Font
            </Button>
          </div>
        </div>
      </div>

      <div className={Styles.mobilePreviewFooter}>
        <Button
          variant="primary"
          size="lg"
          onPress={handleSave}
          isDisabled={isGeneratingStyle}
          className={Styles.mobileFullWidthButton}
        >
          Save Visual Style
        </Button>
      </div>
    </div>
  )
}
